import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { DataTable } from '../components/ui/data-table';
import { Combobox } from '../components/ui/combobox';
import { toast } from 'sonner';
import { 
  Wrench, Plus, Search, AlertTriangle, CheckCircle2, Clock, 
  Calendar, Truck, FileText, XCircle, Edit, Trash2, Eye, 
  ArrowRight, DollarSign, RefreshCw, Upload, Download, History,
  User, Building2, FileUp, X, Paperclip, ChevronRight, Filter,
  BarChart3, TrendingUp, Package, Settings2, AlertCircle, Shield, QrCode
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell 
} from 'recharts';

// ========================================
// COST CHARTS COMPONENT
// ========================================

const CostChartsSection = () => {
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoryStats, setCategoryStats] = useState([]);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const [costRes, categoryRes, inventoryRes] = await Promise.all([
          api.get('/tools/reports/cost-chart-data?months=12'),
          api.get('/tools/reports/category-stats'),
          api.get('/tools/reports/inventory-report')
        ]);
        setChartData(costRes.data.chart_data || []);
        setSummary(costRes.data.summary);
        setCategoryStats(categoryRes.data || []);
        setInventoryReport(inventoryRes.data);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchChartData();
  }, []);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  const formatCurrencyShort = (value) => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value?.toFixed(0) || '0';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-2" />
          <p className="text-slate-500">Se incarca datele...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-600 font-medium">Total Reparatii (12 luni)</p>
              <p className="text-3xl lg:text-4xl font-bold text-blue-700">{summary.total_repairs?.toLocaleString('ro-RO')} RON</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <p className="text-sm text-green-600 font-medium">Total Revizii (12 luni)</p>
              <p className="text-3xl lg:text-4xl font-bold text-green-700">{summary.total_revisions?.toLocaleString('ro-RO')} RON</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <p className="text-sm text-purple-600 font-medium">Cost Total (12 luni)</p>
              <p className="text-3xl lg:text-4xl font-bold text-purple-700">{summary.total?.toLocaleString('ro-RO')} RON</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <p className="text-sm text-orange-600 font-medium">Media Lunara</p>
              <p className="text-3xl lg:text-4xl font-bold text-orange-700">{summary.avg_monthly?.toLocaleString('ro-RO', { maximumFractionDigits: 0 })} RON</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Costs Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Costuri Lunare (Reparatii vs Revizii)
            </CardTitle>
            <CardDescription>Evolutia costurilor pe ultimele 12 luni</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value?.toLocaleString('ro-RO')} RON`, '']}
                    labelFormatter={(label) => `Luna: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="repairs" name="Reparatii" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="revisions" name="Revizii" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Nu exista date pentru perioada selectata
              </div>
            )}
          </CardContent>
        </Card>

        {/* Total Cost Trend Line Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Tendinta Cost Total
            </CardTitle>
            <CardDescription>Costul total lunar (reparatii + revizii)</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value?.toLocaleString('ro-RO')} RON`, 'Cost Total']}
                    labelFormatter={(label) => `Luna: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#8B5CF6" 
                    strokeWidth={3}
                    dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Nu exista date pentru perioada selectata
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" />
              Distributie pe Categorii
            </CardTitle>
            <CardDescription>Numarul de scule per categorie</CardDescription>
          </CardHeader>
          <CardContent>
            {categoryStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryStats}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="category"
                    label={({ category, count }) => `${category}: ${count}`}
                    labelLine={false}
                  >
                    {categoryStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Nu exista categorii definite
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Cost Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Costuri pe Categorii
            </CardTitle>
            <CardDescription>Valoare si costuri reparatii per categorie</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {categoryStats.map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <div>
                      <p className="font-medium">{cat.category}</p>
                      <p className="text-sm text-slate-500">{cat.count} scule</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">{cat.total_value?.toLocaleString('ro-RO')} RON</p>
                    <p className="text-sm text-red-500">Reparatii: {cat.total_repair_cost?.toLocaleString('ro-RO')} RON</p>
                  </div>
                </div>
              ))}
              {categoryStats.length === 0 && (
                <p className="text-center text-slate-400 py-8">Nu exista date</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* INVENTORY REPORT SECTION */}
      {inventoryReport && (
        <div className="space-y-6" data-testid="inventory-report-section">
          <Separator />
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-indigo-600" />
            Raport Inventar Scule
          </h2>

          {/* Inventory KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className="bg-indigo-50 border-indigo-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-indigo-600 font-medium">Total Scule (Serii Unice)</p>
                <p className="text-2xl font-bold text-indigo-700" data-testid="inv-total-tools">{inventoryReport.unique_serials || inventoryReport.total_tools}</p>
              </CardContent>
            </Card>
            <Card className="bg-cyan-50 border-cyan-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-cyan-600 font-medium">Coduri Produs Unice</p>
                <p className="text-2xl font-bold text-cyan-700" data-testid="inv-unique-products">{inventoryReport.unique_products}</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-emerald-600 font-medium">Valoare Achizitie</p>
                <p className="text-2xl font-bold text-emerald-700">{inventoryReport.total_value?.toLocaleString('ro-RO')} RON</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-red-600 font-medium">Cost Reparatii</p>
                <p className="text-2xl font-bold text-red-700">{inventoryReport.total_repair_cost?.toLocaleString('ro-RO')} RON</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-amber-600 font-medium">Cost Revizii</p>
                <p className="text-2xl font-bold text-amber-700">{inventoryReport.total_revision_cost?.toLocaleString('ro-RO')} RON</p>
              </CardContent>
            </Card>
            <Card className="bg-violet-50 border-violet-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-violet-600 font-medium">TCO Total</p>
                <p className="text-2xl font-bold text-violet-700" data-testid="inv-total-tco">{inventoryReport.total_tco?.toLocaleString('ro-RO')} RON</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* TCO per Category Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  TCO per Categorie
                </CardTitle>
                <CardDescription>Cost total de detinere per categorie (achizitie + reparatii + revizii)</CardDescription>
              </CardHeader>
              <CardContent>
                {inventoryReport.categories?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={inventoryReport.categories} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value) => [`${value?.toLocaleString('ro-RO')} RON`, '']} />
                      <Legend />
                      <Bar dataKey="total_value" name="Achizitie" fill="#3B82F6" stackId="tco" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="total_repair_cost" name="Reparatii" fill="#EF4444" stackId="tco" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="total_revision_cost" name="Revizii" fill="#10B981" stackId="tco" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[320px] flex items-center justify-center text-slate-400">Nu exista date</div>
                )}
              </CardContent>
            </Card>

            {/* Category Count Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-teal-600" />
                  Numar Scule per Categorie
                </CardTitle>
                <CardDescription>Distributia echipamentelor pe categorii</CardDescription>
              </CardHeader>
              <CardContent>
                {inventoryReport.categories?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={inventoryReport.categories} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value) => [value, 'Scule']} />
                      <Bar dataKey="count" name="Numar Scule" fill="#6366F1" radius={[0, 6, 6, 0]}>
                        {inventoryReport.categories.map((_, index) => (
                          <Cell key={`cell-cat-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[320px] flex items-center justify-center text-slate-400">Nu exista date</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Products Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                Top Produse (dupa numar de exemplare/serii)
              </CardTitle>
              <CardDescription>Produse grupate pe cod produs - fiecare scula are o serie unica</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="top-products-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="text-left py-2 px-3 font-medium">Cod Produs</th>
                      <th className="text-left py-2 px-3 font-medium">Denumire</th>
                      <th className="text-left py-2 px-3 font-medium">Brand</th>
                      <th className="text-left py-2 px-3 font-medium">Categorie</th>
                      <th className="text-right py-2 px-3 font-medium">Nr. Exemplare (Serii)</th>
                      <th className="text-right py-2 px-3 font-medium">Valoare Totala</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryReport.top_products?.map((p, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-2 px-3 font-mono text-indigo-600">{p.product_code}</td>
                        <td className="py-2 px-3 font-medium">{p.name}</td>
                        <td className="py-2 px-3">{p.brand}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs">{p.category}</Badge>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-bold">{p.count}</span>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold">{p.total_value?.toLocaleString('ro-RO')} RON</td>
                      </tr>
                    ))}
                    {(!inventoryReport.top_products || inventoryReport.top_products.length === 0) && (
                      <tr><td colSpan={6} className="text-center text-slate-400 py-8">Nu exista date</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Category Detail Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Analiza Detaliata per Categorie
              </CardTitle>
              <CardDescription>Valoare achizitie, costuri reparatii/revizii si TCO per categorie</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="category-detail-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="text-left py-2 px-3 font-medium">Categorie</th>
                      <th className="text-right py-2 px-3 font-medium">Nr. Scule</th>
                      <th className="text-right py-2 px-3 font-medium">Valoare Achizitie</th>
                      <th className="text-right py-2 px-3 font-medium">Cost Reparatii</th>
                      <th className="text-right py-2 px-3 font-medium">Cost Revizii</th>
                      <th className="text-right py-2 px-3 font-medium">TCO Total</th>
                      <th className="text-right py-2 px-3 font-medium">Valoare Medie/Scula</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryReport.categories?.map((c, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                            <span className="font-medium">{c.category}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">{c.count}</td>
                        <td className="py-2 px-3 text-right text-blue-600 font-medium">{c.total_value?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right text-red-600">{c.total_repair_cost?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right text-green-600">{c.total_revision_cost?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right font-bold text-violet-700">{c.tco?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right text-slate-600">{c.avg_value_per_tool?.toLocaleString('ro-RO')} RON</td>
                      </tr>
                    ))}
                    {(!inventoryReport.categories || inventoryReport.categories.length === 0) && (
                      <tr><td colSpan={7} className="text-center text-slate-400 py-8">Nu exista date</td></tr>
                    )}
                  </tbody>
                  {inventoryReport.categories?.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                        <td className="py-2 px-3">TOTAL</td>
                        <td className="py-2 px-3 text-right">{inventoryReport.total_tools}</td>
                        <td className="py-2 px-3 text-right text-blue-700">{inventoryReport.total_value?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right text-red-700">{inventoryReport.total_repair_cost?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right text-green-700">{inventoryReport.total_revision_cost?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right text-violet-800">{inventoryReport.total_tco?.toLocaleString('ro-RO')} RON</td>
                        <td className="py-2 px-3 text-right">-</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ========================================
// HELPER FUNCTIONS
// ========================================

const handleApiError = (error) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Eroare la operatie');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('ro-RO');
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(amount);
};

// ========================================
// MAIN COMPONENT
// ========================================

const ToolsPage = () => {
  const { plants, users, locations } = useData();
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  // All revisions and repairs
  const [allRevisions, setAllRevisions] = useState([]);
  const [allRepairs, setAllRepairs] = useState([]);
  const [expiringWarranties, setExpiringWarranties] = useState([]);
  
  // Selected items for bulk operations
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [selectedRevisionIds, setSelectedRevisionIds] = useState([]);
  const [selectedRepairIds, setSelectedRepairIds] = useState([]);
  
  // Dialogs
  const [showToolDialog, setShowToolDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [showRepairDialog, setShowRepairDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCustodyDialog, setShowCustodyDialog] = useState(false);
  const [showTCODialog, setShowTCODialog] = useState(false);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [showEditRepairDialog, setShowEditRepairDialog] = useState(false);
  const [bulkUpdateFields, setBulkUpdateFields] = useState({});
  const [bulkUpdateValues, setBulkUpdateValues] = useState({});
  
  // Selected items
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [toolHistory, setToolHistory] = useState({ revisions: [], repairs: [], timeline: [], documents: [] });
  const [custodyHistory, setCustodyHistory] = useState([]);
  const [tcoData, setTcoData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Forms
  const initialToolForm = {
    name: '', serial_number: '', internal_code: '', category: '', subcategory: '',
    brand: '', model: '', year_of_manufacture: '', purchase_date: '', purchase_cost: '',
    current_value: '', warranty_until: '', supplier: '', product_code: '', plant_id: '', location_id: '', department: '',
    assigned_to: '', assigned_to_name: '', secondary_assigned_to: '', secondary_assigned_to_name: '',
    technical_specs: '', power_rating: '', weight: '', dimensions: '', notes: '', 
    revision_required: false, revision_type: '', revision_interval: '', revision_interval_days: '', 
    next_revision_date: '', certification_body: ''
  };
  
  const [toolForm, setToolForm] = useState(initialToolForm);
  
  const [revisionForm, setRevisionForm] = useState({
    revision_type: 'periodic', revision_date: new Date().toISOString().split('T')[0],
    valid_until: '', performed_by_external: '', external_certificate_number: '',
    cost: '', result: 'passed', findings: '', recommendations: '', notes: '', next_revision_date: ''
  });
  
  const [repairForm, setRepairForm] = useState({
    defect_description: '', defect_type: '', severity: 'minor',
    sent_date: new Date().toISOString().split('T')[0], expected_return_date: '',
    service_provider: '', service_contact: '', service_phone: '', service_email: '',
    service_address: '', estimated_cost: '', warranty_repair: false, notes: ''
  });
  
  const [returnForm, setReturnForm] = useState({
    return_date: new Date().toISOString().split('T')[0],
    actual_cost: '', work_performed: '', parts_replaced: '', notes: ''
  });
  
  const [editRepairForm, setEditRepairForm] = useState({
    defect_description: '', defect_type: '', severity: 'minor',
    sent_date: '', expected_return_date: '', return_date: '',
    service_provider: '', service_contact: '', service_phone: '', service_email: '',
    service_address: '', estimated_cost: '', actual_cost: '', warranty_repair: false, 
    work_performed: '', parts_replaced: '', notes: '', status: 'in_progress'
  });
  
  const [uploadForm, setUploadForm] = useState({
    file: null, document_type: 'certificate', description: ''
  });
  
  const [custodyForm, setCustodyForm] = useState({
    new_custodian: '', new_custodian_name: '', notes: '', transfer_date: new Date().toISOString().split('T')[0]
  });

  // ========================================
  // DATA FETCHING
  // ========================================
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [toolsRes, categoriesRes, deptRes, statsRes] = await Promise.all([
        api.get('/tools'),
        api.get('/tools/categories'),
        api.get('/tools/departments'),
        api.get('/tools/stats')
      ]);
      setTools(toolsRes.data);
      setCategories(categoriesRes.data || []);
      setDepartments(deptRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllRevisions = useCallback(async () => {
    try {
      const res = await api.get('/tools/revisions/all?limit=500');
      setAllRevisions(res.data);
    } catch (error) {
      console.error('Error fetching revisions:', error);
    }
  }, []);

  const fetchAllRepairs = useCallback(async () => {
    try {
      const res = await api.get('/tools/repairs/all?limit=500');
      setAllRepairs(res.data);
    } catch (error) {
      console.error('Error fetching repairs:', error);
    }
  }, []);

  const fetchExpiringWarranties = useCallback(async () => {
    try {
      const res = await api.get('/tools/expiring-warranties?days=60');
      setExpiringWarranties(res.data);
    } catch (error) {
      console.error('Error fetching warranties:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchAllRevisions();
    fetchAllRepairs();
    fetchExpiringWarranties();
  }, [fetchData, fetchAllRevisions, fetchAllRepairs, fetchExpiringWarranties]);

  const fetchToolDetails = async (toolId) => {
    try {
      const [revisionsRes, repairsRes, historyRes, docsRes] = await Promise.all([
        api.get(`/tools/${toolId}/revisions`),
        api.get(`/tools/${toolId}/repairs`),
        api.get(`/tools/${toolId}/history`),
        api.get(`/tools/${toolId}/documents`)
      ]);
      setToolHistory({
        revisions: revisionsRes.data,
        repairs: repairsRes.data,
        timeline: historyRes.data,
        documents: docsRes.data
      });
    } catch (error) {
      console.error('Error fetching tool details:', error);
    }
  };

  const fetchCustodyHistory = async (toolId) => {
    try {
      const res = await api.get(`/tools/${toolId}/custody-history`);
      setCustodyHistory(res.data);
    } catch (error) {
      setCustodyHistory([]);
    }
  };

  const fetchTCO = async (toolId) => {
    try {
      const res = await api.get(`/tools/${toolId}/tco`);
      setTcoData(res.data);
    } catch (error) {
      console.error('Error fetching TCO:', error);
    }
  };

  // ========================================
  // CRUD OPERATIONS
  // ========================================
  
  const handleSaveTool = async () => {
    try {
      const data = { ...toolForm };
      Object.keys(data).forEach(key => { if (data[key] === '') data[key] = null; });
      if (data.purchase_cost) data.purchase_cost = parseFloat(data.purchase_cost);
      if (data.current_value) data.current_value = parseFloat(data.current_value);
      if (data.year_of_manufacture) data.year_of_manufacture = parseInt(data.year_of_manufacture);
      if (data.revision_interval_days) data.revision_interval_days = parseInt(data.revision_interval_days);
      
      if (editMode && selectedTool) {
        await api.put(`/tools/${selectedTool.id}`, data);
        toast.success('Scula actualizata!');
      } else {
        await api.post('/tools', data);
        toast.success('Scula adaugata!');
      }
      setShowToolDialog(false);
      resetToolForm();
      fetchData();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleCreateRevision = async () => {
    try {
      const data = { ...revisionForm, tool_id: selectedTool.id };
      Object.keys(data).forEach(key => { if (data[key] === '') data[key] = null; });
      if (data.cost !== null && data.cost !== undefined) data.cost = parseFloat(data.cost);
      
      await api.post(`/tools/${selectedTool.id}/revisions`, data);
      toast.success('Revizie inregistrata!');
      setShowRevisionDialog(false);
      resetRevisionForm();
      fetchData();
      fetchAllRevisions();
      if (showDetailDialog) fetchToolDetails(selectedTool.id);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleCreateRepair = async () => {
    try {
      const data = { ...repairForm, tool_id: selectedTool.id };
      Object.keys(data).forEach(key => { if (data[key] === '') data[key] = null; });
      if (data.estimated_cost) data.estimated_cost = parseFloat(data.estimated_cost);
      
      await api.post(`/tools/${selectedTool.id}/repairs`, data);
      toast.success('Scula trimisa in service!');
      setShowRepairDialog(false);
      resetRepairForm();
      fetchData();
      fetchAllRepairs();
      if (showDetailDialog) fetchToolDetails(selectedTool.id);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleReturnFromRepair = async () => {
    try {
      const data = {
        ...returnForm, status: 'completed',
        actual_cost: returnForm.actual_cost ? parseFloat(returnForm.actual_cost) : null
      };
      Object.keys(data).forEach(key => { if (data[key] === '') data[key] = null; });
      
      await api.put(`/tools/repairs/${selectedRepair.id}`, data);
      toast.success('Scula returnata din service!');
      setShowReturnDialog(false);
      setSelectedRepair(null);
      fetchData();
      fetchAllRepairs();
      if (showDetailDialog) fetchToolDetails(selectedTool.id);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleTransferCustody = async () => {
    try {
      await api.post(`/tools/${selectedTool.id}/transfer-custody`, {
        new_custodian: custodyForm.new_custodian || null,
        new_custodian_name: custodyForm.new_custodian_name,
        notes: custodyForm.notes || null,
        transfer_date: custodyForm.transfer_date
      });
      toast.success('Transfer custodie inregistrat!');
      setShowCustodyDialog(false);
      setCustodyForm({ new_custodian: '', new_custodian_name: '', notes: '', transfer_date: new Date().toISOString().split('T')[0] });
      fetchData();
      if (showDetailDialog) {
        fetchToolDetails(selectedTool.id);
        fetchCustodyHistory(selectedTool.id);
      }
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta scula si toate datele asociate?')) return;
    try {
      await api.delete(`/tools/${toolId}`);
      toast.success('Scula stearsa!');
      fetchData();
      if (showDetailDialog) setShowDetailDialog(false);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleBulkToolStatus = async (newStatus) => {
    if (!selectedToolIds.length) return;
    const action = newStatus === 'active' ? 'activa' : 'dezactiva';
    if (!window.confirm(`Sigur doriti sa ${action}ti ${selectedToolIds.length} scule?`)) return;
    try {
      await api.patch('/tools/bulk-status', { ids: selectedToolIds, status: newStatus });
      toast.success(`${selectedToolIds.length} scule ${newStatus === 'active' ? 'activate' : 'dezactivate'}`);
      setSelectedToolIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la operatia bulk');
    }
  };

  const handleBulkUpdateFields = async () => {
    const activeFields = Object.keys(bulkUpdateFields).filter(k => bulkUpdateFields[k]);
    if (!activeFields.length) { toast.error('Selectati cel putin un camp'); return; }
    const fields = {};
    activeFields.forEach(k => { fields[k] = bulkUpdateValues[k] ?? ''; });
    try {
      const res = await api.patch('/tools/bulk-update-fields', { ids: selectedToolIds, fields });
      toast.success(`${res.data.modified} scule actualizate (${res.data.fields_updated.length} campuri)`);
      setShowBulkUpdateDialog(false);
      setBulkUpdateFields({});
      setBulkUpdateValues({});
      setSelectedToolIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea in masa');
    }
  };

  const handleBulkDeleteTools = async () => {
    if (!selectedToolIds.length) return;
    if (!window.confirm(`Sigur doriti sa stergeti DEFINITIV ${selectedToolIds.length} scule? Aceasta actiune nu poate fi anulata!`)) return;
    try {
      await api.patch('/tools/bulk-delete', { ids: selectedToolIds });
      toast.success(`${selectedToolIds.length} scule sterse`);
      setSelectedToolIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergerea in masa');
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadForm.file) {
      toast.error('Selectati un fisier');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_type', uploadForm.document_type);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      
      await api.post(`/tools/${selectedTool.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document incarcat!');
      setShowUploadDialog(false);
      setUploadForm({ file: null, document_type: 'certificate', description: '' });
      fetchToolDetails(selectedTool.id);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/tools/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Eroare la descarcare');
    }
  };

  // Export functions
  const handleExportToolsExcel = async () => {
    try {
      const ids = selectedToolIds.length > 0 ? selectedToolIds.join(',') : '';
      const url = ids ? `/tools/reports/export-excel?ids=${ids}` : '/tools/reports/export-excel';
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `scule_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export Excel realizat${selectedToolIds.length > 0 ? ` (${selectedToolIds.length} selectate)` : ''}!`);
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/tools/import-xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erori la import`);
      }
      fetchData();
    } catch (error) {
      toast.error(handleApiError(error));
    }
    e.target.value = '';
  };

  const handleClearAll = async () => {
    if (!window.confirm('ATENTIE! Aceasta actiune va sterge TOATE sculele, reviziile, reparatiile si documentele asociate. Continuati?')) return;
    if (!window.confirm('Sunteti absolut sigur? Aceasta actiune este ireversibila!')) return;
    try {
      const response = await api.delete('/tools/clear-all/confirm');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleExportRevisionsExcel = async () => {
    try {
      const ids = selectedRevisionIds.length > 0 ? selectedRevisionIds.join(',') : '';
      const url = ids ? `/tools/reports/export-revisions-excel?ids=${ids}` : '/tools/reports/export-revisions-excel';
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `revizii_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export revizii Excel realizat!`);
    } catch (error) {
      toast.error('Eroare la export revizii');
    }
  };

  const handleExportRepairsExcel = async () => {
    try {
      const ids = selectedRepairIds.length > 0 ? selectedRepairIds.join(',') : '';
      const url = ids ? `/tools/reports/export-repairs-excel?ids=${ids}` : '/tools/reports/export-repairs-excel';
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `reparatii_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export reparatii Excel realizat!`);
    } catch (error) {
      toast.error('Eroare la export reparatii');
    }
  };

  const handleDeleteRevision = async (revisionId) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta revizie?')) return;
    try {
      await api.delete(`/tools/revisions/${revisionId}`);
      toast.success('Revizie stearsa!');
      fetchAllRevisions();
      fetchData();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleDeleteRepair = async (repairId) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta reparatie?')) return;
    try {
      await api.delete(`/tools/repairs/${repairId}`);
      toast.success('Reparatie stearsa!');
      fetchAllRepairs();
      fetchData();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const openEditRepairDialog = (repair) => {
    setSelectedRepair(repair);
    setEditRepairForm({
      defect_description: repair.defect_description || '',
      defect_type: repair.defect_type || '',
      severity: repair.severity || 'minor',
      sent_date: repair.sent_date ? repair.sent_date.split('T')[0] : '',
      expected_return_date: repair.expected_return_date ? repair.expected_return_date.split('T')[0] : '',
      return_date: repair.return_date ? repair.return_date.split('T')[0] : '',
      service_provider: repair.service_provider || '',
      service_contact: repair.service_contact || '',
      service_phone: repair.service_phone || '',
      service_email: repair.service_email || '',
      service_address: repair.service_address || '',
      estimated_cost: repair.estimated_cost || '',
      actual_cost: repair.actual_cost || '',
      warranty_repair: repair.warranty_repair || false,
      work_performed: repair.work_performed || '',
      parts_replaced: repair.parts_replaced || '',
      notes: repair.notes || '',
      status: repair.status || 'in_progress'
    });
    setShowEditRepairDialog(true);
  };

  const handleEditRepair = async () => {
    try {
      const data = { ...editRepairForm };
      Object.keys(data).forEach(key => { if (data[key] === '') data[key] = null; });
      if (data.estimated_cost) data.estimated_cost = parseFloat(data.estimated_cost);
      if (data.actual_cost) data.actual_cost = parseFloat(data.actual_cost);
      
      await api.put(`/tools/repairs/${selectedRepair.id}`, data);
      toast.success('Reparatie actualizata!');
      setShowEditRepairDialog(false);
      setSelectedRepair(null);
      fetchData();
      fetchAllRepairs();
      if (showDetailDialog && selectedTool) fetchToolDetails(selectedTool.id);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleDownloadCertificate = async (revisionId) => {
    try {
      const response = await api.get(`/tools/revisions/${revisionId}/certificate-pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `certificat_revizie_${revisionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificat PDF descarcat!');
    } catch (error) {
      toast.error('Eroare la generare certificat');
    }
  };

  const [showQrExportDialog, setShowQrExportDialog] = useState(false);
  const [qrExportFormat, setQrExportFormat] = useState('standard');
  const [qrFields, setQrFields] = useState({
    code: true,
    name: true,
    revision_date: true,
    location: true,
    serial: false,
    category: false,
    brand: false,
    custodian: false
  });

  const qrFieldOptions = [
    { key: 'code', label: 'Cod / Nr. Scula' },
    { key: 'name', label: 'Denumire' },
    { key: 'revision_date', label: 'Data urmatoarei revizii' },
    { key: 'location', label: 'Sediu / Locatie' },
    { key: 'serial', label: 'Serie' },
    { key: 'category', label: 'Categorie' },
    { key: 'brand', label: 'Brand / Model' },
    { key: 'custodian', label: 'Responsabil (Custode)' }
  ];

  const handleExportQrCodes = async (format = 'standard') => {
    try {
      const ids = selectedToolIds.length > 0 ? selectedToolIds : tools.map(t => t.id);
      const fields = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const response = await api.post(`/tools/qr-export?format=${format}&fields=${fields}`, ids, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `qr_scule_${ids.length}_${format}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowQrExportDialog(false);
      toast.success(`Export QR (${format}) pentru ${ids.length} scule generat!`);
    } catch (error) {
      toast.error('Eroare la export QR');
    }
  };

  const handleExportQrZip = async () => {
    try {
      const ids = selectedToolIds.length > 0 ? selectedToolIds : tools.map(t => t.id);
      const fields = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const response = await api.post(`/tools/qr-export-zip?fields=${fields}`, ids, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `qr_scule_${ids.length}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowQrExportDialog(false);
      toast.success(`Arhiva ZIP cu ${ids.length} imagini QR generata!`);
    } catch (error) {
      toast.error('Eroare la export ZIP');
    }
  };

  // ========================================
  // FORM HELPERS
  // ========================================
  
  const resetToolForm = () => {
    setToolForm(initialToolForm);
    setEditMode(false);
    setSelectedTool(null);
  };

  const resetRevisionForm = () => {
    setRevisionForm({
      revision_type: 'periodic', revision_date: new Date().toISOString().split('T')[0],
      valid_until: '', performed_by_external: '', external_certificate_number: '',
      cost: '', result: 'passed', findings: '', recommendations: '', notes: '', next_revision_date: ''
    });
  };

  const resetRepairForm = () => {
    setRepairForm({
      defect_description: '', defect_type: '', severity: 'minor',
      sent_date: new Date().toISOString().split('T')[0], expected_return_date: '',
      service_provider: '', service_contact: '', service_phone: '', service_email: '',
      service_address: '', estimated_cost: '', warranty_repair: false, notes: ''
    });
  };

  const openEditDialog = (tool) => {
    setSelectedTool(tool);
    setToolForm({
      name: tool.name || '', serial_number: tool.serial_number || '',
      internal_code: tool.internal_code || '', category: tool.category || '',
      subcategory: tool.subcategory || '', brand: tool.brand || '', model: tool.model || '',
      year_of_manufacture: tool.year_of_manufacture || '',
      purchase_date: tool.purchase_date?.split('T')[0] || '',
      purchase_cost: tool.purchase_cost || '', current_value: tool.current_value || '',
      warranty_until: tool.warranty_until?.split('T')[0] || '', supplier: tool.supplier || '',
      product_code: tool.product_code || '',
      plant_id: tool.plant_id || '', location_id: tool.location_id || '', department: tool.department || '',
      assigned_to: tool.assigned_to || '', assigned_to_name: tool.assigned_to_name || '',
      secondary_assigned_to: tool.secondary_assigned_to || '', 
      secondary_assigned_to_name: tool.secondary_assigned_to_name || '',
      technical_specs: tool.technical_specs || '', power_rating: tool.power_rating || '',
      weight: tool.weight || '', dimensions: tool.dimensions || '', notes: tool.notes || '',
      revision_required: tool.revision_required || false, revision_type: tool.revision_type || '',
      revision_interval: tool.revision_interval || '',
      revision_interval_days: tool.revision_interval_days || '',
      next_revision_date: tool.next_revision_date?.split('T')[0] || '',
      certification_body: tool.certification_body || ''
    });
    setEditMode(true);
    setShowToolDialog(true);
  };

  const openDetailDialog = async (tool) => {
    setSelectedTool(tool);
    await fetchToolDetails(tool.id);
    await fetchCustodyHistory(tool.id);
    setShowDetailDialog(true);
  };

  const openTCODialog = async (tool) => {
    setSelectedTool(tool);
    await fetchTCO(tool.id);
    setShowTCODialog(true);
  };

  // ========================================
  // UI HELPERS
  // ========================================
  
  const getRevisionStatusBadge = (tool) => {
    if (!tool.revision_required) return null;
    const status = tool.revision_status;
    const days = tool.days_until_revision;
    
    if (!status) {
      return <Badge className="bg-slate-100 text-slate-600"><Clock className="w-3 h-3 mr-1" />Nesetat</Badge>;
    }
    
    const styles = {
      ok: { bg: 'bg-green-100 text-green-700', icon: CheckCircle2, label: `${days} zile` },
      upcoming: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: `${days} zile` },
      warning: { bg: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle, label: `${days} zile` },
      critical: { bg: 'bg-orange-100 text-orange-700', icon: AlertCircle, label: `${days} zile` },
      expired: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Expirat' }
    };
    
    const style = styles[status] || styles.ok;
    const Icon = style.icon;
    return <Badge className={style.bg}><Icon className="w-3 h-3 mr-1" />{style.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-green-100 text-green-700', label: 'Activ' },
      in_service: { bg: 'bg-yellow-100 text-yellow-700', label: 'In Service' },
      retired: { bg: 'bg-slate-100 text-slate-700', label: 'Retras' },
      lost: { bg: 'bg-red-100 text-red-700', label: 'Pierdut' },
      scrapped: { bg: 'bg-gray-100 text-gray-700', label: 'Casat' }
    };
    const style = styles[status] || styles.active;
    return <Badge className={style.bg}>{style.label}</Badge>;
  };

  const getDocumentTypeBadge = (type) => {
    const styles = {
      certificate: { bg: 'bg-blue-100 text-blue-700', label: 'Certificat' },
      invoice: { bg: 'bg-green-100 text-green-700', label: 'Factura' },
      manual: { bg: 'bg-purple-100 text-purple-700', label: 'Manual' },
      photo: { bg: 'bg-orange-100 text-orange-700', label: 'Fotografie' },
      warranty: { bg: 'bg-teal-100 text-teal-700', label: 'Garantie' },
      report: { bg: 'bg-indigo-100 text-indigo-700', label: 'Raport' },
      other: { bg: 'bg-slate-100 text-slate-700', label: 'Altele' }
    };
    const style = styles[type] || styles.other;
    return <Badge className={style.bg}>{style.label}</Badge>;
  };

  // DataTable columns
  const toolColumns = [
    { key: 'tool_number', label: 'Nr. Scula', sortable: true, filterable: true, width: '120px' },
    { key: 'name', label: 'Denumire', sortable: true, filterable: true },
    { key: 'serial_number', label: 'Serie', sortable: true, filterable: true, width: '130px' },
    { key: 'brand', label: 'Brand', sortable: true, filterable: true, width: '100px' },
    { key: 'product_code', label: 'Cod Produs', sortable: true, filterable: true, width: '120px' },
    { key: 'category', label: 'Categorie', sortable: true, filterable: true, width: '120px' },
    { key: 'status', label: 'Status', sortable: true, filterable: true, width: '100px',
      render: (val) => getStatusBadge(val) },
    { key: 'revision_status', label: 'Revizie', sortable: true, width: '110px',
      render: (val, row) => getRevisionStatusBadge(row) },
    { key: 'assigned_to_name', label: 'Responsabil', sortable: true, filterable: true, width: '140px' },
    { key: 'purchase_cost', label: 'Cost', sortable: true, width: '100px',
      render: (val) => formatCurrency(val) },
    { key: 'actions', label: 'Actiuni', width: '180px',
      render: (_, row) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetailDialog(row); }} data-testid={`tool-view-${row.id}`}><Eye className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(row); }} data-testid={`tool-edit-${row.id}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openTCODialog(row); }} title="TCO"><DollarSign className="w-4 h-4 text-purple-600" /></Button>
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleDeleteTool(row.id); }} title="Sterge" data-testid={`tool-delete-${row.id}`}><Trash2 className="w-4 h-4" /></Button>
          {row.status === 'active' && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedTool(row); setShowRevisionDialog(true); }} title="Revizie">
              <RefreshCw className="w-4 h-4 text-green-600" />
            </Button>
          )}
        </div>
      )
    }
  ];

  const revisionColumns = [
    { key: 'revision_number', label: 'Nr. Revizie', sortable: true, filterable: true, width: '140px' },
    { key: 'tool_name', label: 'Scula', sortable: true, filterable: true },
    { key: 'tool_serial', label: 'Serie', sortable: true, width: '120px' },
    { key: 'revision_type', label: 'Tip', sortable: true, filterable: true, width: '100px' },
    { key: 'revision_date', label: 'Data', sortable: true, width: '100px', render: (val) => formatDate(val) },
    { key: 'performed_by_external', label: 'Efectuat de', sortable: true, filterable: true, width: '140px' },
    { key: 'cost', label: 'Cost', sortable: true, width: '100px', render: (val) => formatCurrency(val) },
    { key: 'result', label: 'Rezultat', sortable: true, width: '100px',
      render: (val) => (
        <Badge className={val === 'passed' ? 'bg-green-100 text-green-700' : 
                         val === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
          {val === 'passed' ? 'Admis' : val === 'failed' ? 'Respins' : 'Conditionat'}
        </Badge>
      )
    },
    { key: 'actions', label: 'Actiuni', width: '120px',
      render: (_, row) => (
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => { e.stopPropagation(); handleDownloadCertificate(row.id); }}
            title="Descarca Certificat PDF"
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            data-testid={`revision-cert-${row.id}`}
          >
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => { e.stopPropagation(); handleDeleteRevision(row.id); }}
            title="Sterge revizia"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`revision-delete-${row.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const repairColumns = [
    { key: 'repair_number', label: 'Nr. Reparatie', sortable: true, filterable: true, width: '140px' },
    { key: 'tool_name', label: 'Scula', sortable: true, filterable: true },
    { key: 'defect_description', label: 'Defect', sortable: true, filterable: true, 
      render: (val) => <span className="truncate max-w-[200px] block">{val?.substring(0, 40)}...</span> },
    { key: 'service_provider', label: 'Service', sortable: true, filterable: true, width: '140px' },
    { key: 'sent_date', label: 'Trimis', sortable: true, width: '100px', render: (val) => formatDate(val) },
    { key: 'actual_cost', label: 'Cost', sortable: true, width: '100px', 
      render: (val, row) => val ? formatCurrency(val) : (row.estimated_cost ? `~${formatCurrency(row.estimated_cost)}` : '-') },
    { key: 'status', label: 'Status', sortable: true, filterable: true, width: '100px',
      render: (val) => (
        <Badge className={val === 'completed' ? 'bg-green-100 text-green-700' : 
                         val === 'cancelled' ? 'bg-gray-100 text-gray-700' : 'bg-yellow-100 text-yellow-700'}>
          {val === 'completed' ? 'Finalizat' : val === 'cancelled' ? 'Anulat' : 'In curs'}
        </Badge>
      )
    },
    { key: 'actions', label: 'Actiuni', width: '80px',
      render: (_, row) => (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={(e) => { e.stopPropagation(); handleDeleteRepair(row.id); }}
          title="Sterge reparatia"
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
          data-testid={`repair-delete-${row.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )
    }
  ];

  const toolsInService = tools.filter(t => t.status === 'in_service');
  const expiringTools = tools.filter(t => ['critical', 'expired', 'warning'].includes(t.revision_status));

  // ========================================
  // RENDER
  // ========================================
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-7 h-7 text-blue-600" />
            Scule de Lucru
          </h1>
          <p className="text-slate-500">Gestionare scule, revizii, reparatii si documente</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowQrExportDialog(true)}>
            <QrCode className="w-4 h-4 mr-2" />Print QR {selectedToolIds.length > 0 && `(${selectedToolIds.length})`}
          </Button>
          <Button variant="outline" onClick={handleExportToolsExcel}>
            <Download className="w-4 h-4 mr-2" />Export Excel {selectedToolIds.length > 0 && `(${selectedToolIds.length})`}
          </Button>
          <Button variant="outline" className="relative" data-testid="import-excel-btn">
            <Upload className="w-4 h-4 mr-2" />Import Excel
            <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="absolute inset-0 opacity-0 cursor-pointer" />
          </Button>
          <Button onClick={() => { resetToolForm(); setShowToolDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />Adauga Scula
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card><CardContent className="p-4 text-center">
            <Package className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{stats.total_tools}</p>
            <p className="text-xs text-slate-500">Total Scule</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{stats.active}</p>
            <p className="text-xs text-slate-500">Active</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Truck className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{stats.in_service}</p>
            <p className="text-xs text-slate-500">In Service</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto text-red-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{(stats.revision_stats?.critical || 0) + (stats.revision_stats?.expired || 0)}</p>
            <p className="text-xs text-slate-500">Revizii Urgente</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Shield className="w-8 h-8 mx-auto text-orange-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{expiringWarranties.length}</p>
            <p className="text-xs text-slate-500">Garantii Expira</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <DollarSign className="w-8 h-8 mx-auto text-purple-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{formatCurrency(stats.total_repair_cost)}</p>
            <p className="text-xs text-slate-500">Cost Reparatii</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-teal-500 mb-2" />
            <p className="text-3xl lg:text-4xl font-bold">{formatCurrency(stats.total_value)}</p>
            <p className="text-xs text-slate-500">Valoare Totala</p>
          </CardContent></Card>
        </div>
      )}

      {/* Alerts */}
      {(expiringTools.length > 0 || expiringWarranties.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {expiringTools.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Revizii care necesita atentie ({expiringTools.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expiringTools.slice(0, 5).map(tool => (
                    <Badge key={tool.id} variant="outline" className="bg-white cursor-pointer hover:bg-red-100" 
                           onClick={() => openDetailDialog(tool)}>
                      {tool.tool_number || tool.serial_number} - {tool.name}
                    </Badge>
                  ))}
                  {expiringTools.length > 5 && <Badge variant="outline">+{expiringTools.length - 5} altele</Badge>}
                </div>
              </CardContent>
            </Card>
          )}
          {expiringWarranties.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-orange-700 mb-2">
                  <Shield className="w-5 h-5" />
                  <span className="font-semibold">Garantii care expira ({expiringWarranties.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expiringWarranties.slice(0, 5).map(tool => (
                    <Badge key={tool.id} variant="outline" className="bg-white cursor-pointer hover:bg-orange-100"
                           onClick={() => openDetailDialog(tools.find(t => t.id === tool.id) || tool)}>
                      {tool.name} - {tool.days_remaining} zile
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
          <TabsTrigger value="list">Lista Scule</TabsTrigger>
          <TabsTrigger value="all_revisions">Revizii ({allRevisions.length})</TabsTrigger>
          <TabsTrigger value="all_repairs">Reparatii ({allRepairs.length})</TabsTrigger>
          <TabsTrigger value="in_service">In Service ({toolsInService.length})</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="reports">Rapoarte</TabsTrigger>
        </TabsList>

        {/* LIST TAB with DataTable */}
        <TabsContent value="list" className="space-y-4">
          {selectedToolIds.length > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="bulk-actions-tools">
              <span className="text-sm font-medium text-blue-800">{selectedToolIds.length} selectate</span>
              <div className="flex gap-2 ml-auto flex-wrap">
                <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => { setBulkUpdateFields({}); setBulkUpdateValues({}); setShowBulkUpdateDialog(true); }} data-testid="bulk-update-fields-tools">
                  <RefreshCw className="w-4 h-4 mr-1" /> Actualizare in masa
                </Button>
                <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleBulkToolStatus('active')} data-testid="bulk-activate-tools">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Activeaza
                </Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleBulkToolStatus('retired')} data-testid="bulk-deactivate-tools">
                  <XCircle className="w-4 h-4 mr-1" /> Dezactiveaza (Retired)
                </Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={handleBulkDeleteTools} data-testid="bulk-delete-tools">
                  <Trash2 className="w-4 h-4 mr-1" /> Sterge
                </Button>
              </div>
            </div>
          )}
          <DataTable
            data={tools}
            columns={toolColumns}
            pageSize={20}
            searchable={true}
            exportable={false}
            selectable={true}
            onSelectionChange={(ids) => setSelectedToolIds(ids)}
            onRowClick={(row) => openDetailDialog(row)}
            emptyMessage="Nu exista scule inregistrate"
          />
        </TabsContent>

        {/* ALL REVISIONS TAB with DataTable */}
        <TabsContent value="all_revisions" className="space-y-4">
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={handleExportRevisionsExcel}>
              <Download className="w-4 h-4 mr-2" />Export Excel {selectedRevisionIds.length > 0 && `(${selectedRevisionIds.length})`}
            </Button>
          </div>
          <DataTable
            data={allRevisions}
            columns={revisionColumns}
            pageSize={20}
            searchable={true}
            selectable={true}
            onSelectionChange={(ids) => setSelectedRevisionIds(ids)}
            emptyMessage="Nu exista revizii inregistrate"
          />
        </TabsContent>

        {/* ALL REPAIRS TAB with DataTable */}
        <TabsContent value="all_repairs" className="space-y-4">
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={handleExportRepairsExcel}>
              <Download className="w-4 h-4 mr-2" />Export Excel {selectedRepairIds.length > 0 && `(${selectedRepairIds.length})`}
            </Button>
          </div>
          <DataTable
            data={allRepairs}
            columns={repairColumns}
            pageSize={20}
            searchable={true}
            selectable={true}
            onSelectionChange={(ids) => setSelectedRepairIds(ids)}
            emptyMessage="Nu exista reparatii inregistrate"
          />
        </TabsContent>

        {/* IN SERVICE TAB */}
        <TabsContent value="in_service" className="space-y-4">
          {toolsInService.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-300 mb-4" />
              <p className="text-slate-500">Nu exista scule in service</p>
            </CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {toolsInService.map(tool => (
                <Card key={tool.id} className="border-yellow-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-mono text-slate-400">{tool.tool_number}</span>
                        <h3 className="font-semibold">{tool.name}</h3>
                        <p className="text-sm text-slate-500">S/N: {tool.serial_number}</p>
                      </div>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700"
                        onClick={async () => {
                          const repairs = await api.get(`/tools/${tool.id}/repairs`);
                          const activeRepair = repairs.data.find(r => !['completed', 'cancelled'].includes(r.status));
                          if (activeRepair) {
                            setSelectedTool(tool);
                            setSelectedRepair(activeRepair);
                            setShowReturnDialog(true);
                          }
                        }}>
                        <ArrowRight className="w-4 h-4 mr-1" />Returnare
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CALENDAR TAB */}
        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Revizii Programate</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tools.filter(t => t.revision_required && t.next_revision_date)
                  .sort((a, b) => new Date(a.next_revision_date) - new Date(b.next_revision_date))
                  .slice(0, 20)
                  .map(tool => (
                  <div key={tool.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                       onClick={() => openDetailDialog(tool)}>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium">{tool.name}</p>
                        <p className="text-sm text-slate-500">{tool.tool_number} | {tool.serial_number}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatDate(tool.next_revision_date)}</p>
                      {getRevisionStatusBadge(tool)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTS TAB */}
        <TabsContent value="reports" className="space-y-6">
          {/* Cost Charts Section */}
          <CostChartsSection />
          
          {/* Export Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={handleExportToolsExcel}>
              <CardContent className="p-6 text-center">
                <Download className="w-10 h-10 mx-auto text-blue-500 mb-3" />
                <h3 className="font-semibold">Export Lista Scule</h3>
                <p className="text-sm text-slate-500">Descarca toate sculele in format Excel</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={handleExportRevisionsExcel}>
              <CardContent className="p-6 text-center">
                <RefreshCw className="w-10 h-10 mx-auto text-green-500 mb-3" />
                <h3 className="font-semibold">Export Revizii</h3>
                <p className="text-sm text-slate-500">Descarca toate reviziile in format Excel</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={handleExportRepairsExcel}>
              <CardContent className="p-6 text-center">
                <Truck className="w-10 h-10 mx-auto text-orange-500 mb-3" />
                <h3 className="font-semibold">Export Reparatii</h3>
                <p className="text-sm text-slate-500">Descarca toate reparatiile in format Excel</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ADD/EDIT TOOL DIALOG */}
      <Dialog open={showToolDialog} onOpenChange={(open) => { setShowToolDialog(open); if (!open) resetToolForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editare Scula' : 'Adauga Scula Noua'}</DialogTitle>
            <DialogDescription>Completati informatiile despre scula</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="technical">Tehnic</TabsTrigger>
              <TabsTrigger value="location">Locatie</TabsTrigger>
              <TabsTrigger value="revision">Revizie</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Denumire *</Label>
                  <Input value={toolForm.name} onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })} 
                         placeholder="Ex: Compresor Aer Industrial" />
                </div>
                <div className="space-y-2">
                  <Label>Numar Serie *</Label>
                  <Input value={toolForm.serial_number} onChange={(e) => setToolForm({ ...toolForm, serial_number: e.target.value })} 
                         placeholder="Ex: CMP-2024-001" />
                </div>
                <div className="space-y-2">
                  <Label>Cod Intern</Label>
                  <Input value={toolForm.internal_code} onChange={(e) => setToolForm({ ...toolForm, internal_code: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Categorie</Label>
                  <Input value={toolForm.category} onChange={(e) => setToolForm({ ...toolForm, category: e.target.value })} 
                         placeholder="Ex: Compresoare" list="categories" />
                  <datalist id="categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
                </div>
                <div className="space-y-2">
                  <Label>Brand</Label>
                  <Input value={toolForm.brand} onChange={(e) => setToolForm({ ...toolForm, brand: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Input value={toolForm.model} onChange={(e) => setToolForm({ ...toolForm, model: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>An fabricatie</Label>
                  <Input type="number" value={toolForm.year_of_manufacture} 
                         onChange={(e) => setToolForm({ ...toolForm, year_of_manufacture: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Furnizor</Label>
                  <Input value={toolForm.supplier} onChange={(e) => setToolForm({ ...toolForm, supplier: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Cod Produs</Label>
                  <Input value={toolForm.product_code} onChange={(e) => setToolForm({ ...toolForm, product_code: e.target.value })} placeholder="Ex: 2330509" data-testid="product-code-input" />
                </div>
                <div className="space-y-2">
                  <Label>Data achizitie</Label>
                  <Input type="date" value={toolForm.purchase_date} 
                         onChange={(e) => setToolForm({ ...toolForm, purchase_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Cost achizitie (RON)</Label>
                  <Input type="number" step="0.01" value={toolForm.purchase_cost} 
                         onChange={(e) => setToolForm({ ...toolForm, purchase_cost: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Valoare curenta (RON)</Label>
                  <Input type="number" step="0.01" value={toolForm.current_value} 
                         onChange={(e) => setToolForm({ ...toolForm, current_value: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Garantie pana la</Label>
                  <Input type="date" value={toolForm.warranty_until} 
                         onChange={(e) => setToolForm({ ...toolForm, warranty_until: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={toolForm.notes} onChange={(e) => setToolForm({ ...toolForm, notes: e.target.value })} rows={2} />
              </div>
            </TabsContent>
            
            <TabsContent value="technical" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Putere</Label>
                  <Input value={toolForm.power_rating} onChange={(e) => setToolForm({ ...toolForm, power_rating: e.target.value })} 
                         placeholder="Ex: 2200W, 3HP" />
                </div>
                <div className="space-y-2">
                  <Label>Greutate</Label>
                  <Input value={toolForm.weight} onChange={(e) => setToolForm({ ...toolForm, weight: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Dimensiuni</Label>
                  <Input value={toolForm.dimensions} onChange={(e) => setToolForm({ ...toolForm, dimensions: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Specificatii tehnice</Label>
                <Textarea value={toolForm.technical_specs} onChange={(e) => setToolForm({ ...toolForm, technical_specs: e.target.value })} 
                          rows={4} placeholder="Descriere detaliata..." />
              </div>
            </TabsContent>
            
            <TabsContent value="location" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sediu</Label>
                  <Combobox
                    items={plants || []}
                    value={toolForm.plant_id || ''}
                    onSelect={(id) => setToolForm({ ...toolForm, plant_id: id, location_id: '' })}
                    placeholder="Selecteaza sediul..."
                    searchPlaceholder="Cauta sediu..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Locatie</Label>
                  <Combobox
                    items={(locations || []).filter(l => !toolForm.plant_id || l.plant_id === toolForm.plant_id)}
                    value={toolForm.location_id || ''}
                    onSelect={(id) => setToolForm({ ...toolForm, location_id: id })}
                    placeholder="Selecteaza locatia..."
                    searchPlaceholder="Cauta locatie..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Departament</Label>
                  <Input value={toolForm.department} onChange={(e) => setToolForm({ ...toolForm, department: e.target.value })} 
                         list="departments" placeholder="Ex: Productie, Mentenanta..." />
                  <datalist id="departments">{departments.map(d => <option key={d} value={d} />)}</datalist>
                </div>
                <div className="space-y-2">
                  <Label>Responsabil Principal (Custode)</Label>
                  <Input value={toolForm.assigned_to_name || ''} 
                         onChange={(e) => setToolForm({ ...toolForm, assigned_to_name: e.target.value })} 
                         placeholder="Introduceti numele responsabilului" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Responsabil Secundar</Label>
                  <Input value={toolForm.secondary_assigned_to_name || ''} 
                         onChange={(e) => setToolForm({ ...toolForm, secondary_assigned_to_name: e.target.value })} 
                         placeholder="Introduceti numele (optional)" />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="revision" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <Checkbox checked={toolForm.revision_required} 
                          onCheckedChange={(v) => setToolForm({ ...toolForm, revision_required: v })} />
                <Label>Necesita revizii periodice</Label>
              </div>
              
              {toolForm.revision_required && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Tip revizie</Label>
                    <Select value={toolForm.revision_type || ''} onValueChange={(v) => setToolForm({ ...toolForm, revision_type: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="periodic">Periodica</SelectItem>
                        <SelectItem value="mandatory">Obligatorie (ISCIR)</SelectItem>
                        <SelectItem value="calibration">Calibrare</SelectItem>
                        <SelectItem value="inspection">Inspectie</SelectItem>
                        <SelectItem value="certification">Certificare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Interval</Label>
                    <Select value={toolForm.revision_interval || ''} onValueChange={(v) => setToolForm({ ...toolForm, revision_interval: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Lunar (30 zile)</SelectItem>
                        <SelectItem value="quarterly">Trimestrial (90 zile)</SelectItem>
                        <SelectItem value="biannual">Semestrial (180 zile)</SelectItem>
                        <SelectItem value="annual">Anual (365 zile)</SelectItem>
                        <SelectItem value="biennial">Bienal (2 ani)</SelectItem>
                        <SelectItem value="custom">Personalizat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {toolForm.revision_interval === 'custom' && (
                    <div className="space-y-2">
                      <Label>Interval (zile)</Label>
                      <Input type="number" value={toolForm.revision_interval_days} 
                             onChange={(e) => setToolForm({ ...toolForm, revision_interval_days: e.target.value })} />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Urmatoarea revizie</Label>
                    <Input type="date" value={toolForm.next_revision_date} 
                           onChange={(e) => setToolForm({ ...toolForm, next_revision_date: e.target.value })} />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Organism certificare</Label>
                    <Input value={toolForm.certification_body} 
                           onChange={(e) => setToolForm({ ...toolForm, certification_body: e.target.value })} 
                           placeholder="Ex: ISCIR, RAR, etc." />
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowToolDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveTool} className="bg-blue-600 hover:bg-blue-700">
              {editMode ? 'Salveaza' : 'Adauga'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVISION DIALOG */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Inregistrare Revizie</DialogTitle>
            <DialogDescription>{selectedTool?.name} ({selectedTool?.serial_number})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tip revizie</Label>
                <Select value={revisionForm.revision_type} onValueChange={(v) => setRevisionForm({ ...revisionForm, revision_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="periodic">Periodica</SelectItem>
                    <SelectItem value="mandatory">Obligatorie</SelectItem>
                    <SelectItem value="calibration">Calibrare</SelectItem>
                    <SelectItem value="inspection">Inspectie</SelectItem>
                    <SelectItem value="certification">Certificare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rezultat</Label>
                <Select value={revisionForm.result || 'passed'} onValueChange={(v) => setRevisionForm({ ...revisionForm, result: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passed">Admis</SelectItem>
                    <SelectItem value="conditional">Conditionat</SelectItem>
                    <SelectItem value="failed">Respins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data revizie</Label>
                <Input type="date" value={revisionForm.revision_date} 
                       onChange={(e) => setRevisionForm({ ...revisionForm, revision_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Valabil pana la</Label>
                <Input type="date" value={revisionForm.valid_until} 
                       onChange={(e) => setRevisionForm({ ...revisionForm, valid_until: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Efectuata de (extern)</Label>
                <Input value={revisionForm.performed_by_external} 
                       onChange={(e) => setRevisionForm({ ...revisionForm, performed_by_external: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Nr. certificat extern</Label>
                <Input value={revisionForm.external_certificate_number} 
                       onChange={(e) => setRevisionForm({ ...revisionForm, external_certificate_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cost (RON)</Label>
                <Input type="number" step="0.01" value={revisionForm.cost} 
                       onChange={(e) => setRevisionForm({ ...revisionForm, cost: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Urmatoarea revizie</Label>
                <Input type="date" value={revisionForm.next_revision_date} 
                       onChange={(e) => setRevisionForm({ ...revisionForm, next_revision_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Constatari</Label>
              <Textarea value={revisionForm.findings} onChange={(e) => setRevisionForm({ ...revisionForm, findings: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Recomandari</Label>
              <Textarea value={revisionForm.recommendations} onChange={(e) => setRevisionForm({ ...revisionForm, recommendations: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>Anuleaza</Button>
            <Button onClick={handleCreateRevision} className="bg-green-600 hover:bg-green-700">Salveaza Revizie</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPAIR DIALOG */}
      <Dialog open={showRepairDialog} onOpenChange={setShowRepairDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Trimite in Service</DialogTitle>
            <DialogDescription>{selectedTool?.name} ({selectedTool?.serial_number})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Descriere defect *</Label>
              <Textarea value={repairForm.defect_description} 
                        onChange={(e) => setRepairForm({ ...repairForm, defect_description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tip defect</Label>
                <Select value={repairForm.defect_type || ''} onValueChange={(v) => setRepairForm({ ...repairForm, defect_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mechanical">Mecanic</SelectItem>
                    <SelectItem value="electrical">Electric</SelectItem>
                    <SelectItem value="wear">Uzura</SelectItem>
                    <SelectItem value="calibration">Calibrare</SelectItem>
                    <SelectItem value="other">Altele</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severitate</Label>
                <Select value={repairForm.severity || 'minor'} onValueChange={(v) => setRepairForm({ ...repairForm, severity: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data trimitere</Label>
                <Input type="date" value={repairForm.sent_date} 
                       onChange={(e) => setRepairForm({ ...repairForm, sent_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Data estimata retur</Label>
                <Input type="date" value={repairForm.expected_return_date} 
                       onChange={(e) => setRepairForm({ ...repairForm, expected_return_date: e.target.value })} />
              </div>
            </div>
            <Separator />
            <p className="text-sm font-medium text-slate-700">Furnizor Service</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nume firma</Label>
                <Input value={repairForm.service_provider} 
                       onChange={(e) => setRepairForm({ ...repairForm, service_provider: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Persoana contact</Label>
                <Input value={repairForm.service_contact} 
                       onChange={(e) => setRepairForm({ ...repairForm, service_contact: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={repairForm.service_phone} 
                       onChange={(e) => setRepairForm({ ...repairForm, service_phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cost estimat (RON)</Label>
                <Input type="number" step="0.01" value={repairForm.estimated_cost} 
                       onChange={(e) => setRepairForm({ ...repairForm, estimated_cost: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox checked={repairForm.warranty_repair} 
                          onCheckedChange={(v) => setRepairForm({ ...repairForm, warranty_repair: v })} />
                <Label>Reparatie in garantie</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepairDialog(false)}>Anuleaza</Button>
            <Button onClick={handleCreateRepair} className="bg-orange-600 hover:bg-orange-700">Trimite in Service</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RETURN FROM REPAIR DIALOG */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Returnare din Service</DialogTitle>
            <DialogDescription>{selectedTool?.name} - {selectedRepair?.repair_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data returnare</Label>
                <Input type="date" value={returnForm.return_date} 
                       onChange={(e) => setReturnForm({ ...returnForm, return_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cost final (RON)</Label>
                <Input type="number" step="0.01" value={returnForm.actual_cost} 
                       onChange={(e) => setReturnForm({ ...returnForm, actual_cost: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Lucrari efectuate</Label>
              <Textarea value={returnForm.work_performed} 
                        onChange={(e) => setReturnForm({ ...returnForm, work_performed: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Piese inlocuite</Label>
              <Textarea value={returnForm.parts_replaced} 
                        onChange={(e) => setReturnForm({ ...returnForm, parts_replaced: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>Anuleaza</Button>
            <Button onClick={handleReturnFromRepair} className="bg-green-600 hover:bg-green-700">Confirma Returnare</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TCO DIALOG */}
      <Dialog open={showTCODialog} onOpenChange={setShowTCODialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              TCO - Total Cost of Ownership
            </DialogTitle>
            <DialogDescription>{tcoData?.tool_name} ({tcoData?.serial_number})</DialogDescription>
          </DialogHeader>
          {tcoData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-500">Cost Achizitie</p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(tcoData.purchase_cost)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-500">Cost Reparatii</p>
                    <p className="text-xl font-bold text-orange-700">{formatCurrency(tcoData.total_repair_cost)}</p>
                    <p className="text-xs text-slate-400">{tcoData.repair_count} reparatii</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-500">Cost Revizii</p>
                    <p className="text-xl font-bold text-green-700">{formatCurrency(tcoData.total_revision_cost)}</p>
                    <p className="text-xs text-slate-400">{tcoData.revision_count} revizii</p>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-slate-500">TOTAL COST OF OWNERSHIP (TCO)</p>
                  <p className="text-3xl font-bold text-purple-700">{formatCurrency(tcoData.total_tco)}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Cost anual mediu: {formatCurrency(tcoData.annual_cost)} ({tcoData.ownership_years} ani)
                  </p>
                </CardContent>
              </Card>
              
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Amortizare</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold">{formatCurrency(tcoData.depreciation)}</span>
                      <Badge className="bg-red-100 text-red-700">-{tcoData.depreciation_percent}%</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatCurrency(tcoData.purchase_cost)} → {formatCurrency(tcoData.current_value)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Perioada Detinere</p>
                    <p className="text-lg font-bold">{tcoData.ownership_days} zile</p>
                    <p className="text-xs text-slate-400">({tcoData.ownership_years} ani)</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTCODialog(false)}>Inchide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-blue-600" />
                  {selectedTool?.name}
                </DialogTitle>
                <DialogDescription>
                  {selectedTool?.tool_number} | S/N: {selectedTool?.serial_number}
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                {selectedTool?.status === 'active' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setShowUploadDialog(true)}>
                      <Upload className="w-4 h-4 mr-1" />Document
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openTCODialog(selectedTool)}>
                      <DollarSign className="w-4 h-4 mr-1" />TCO
                    </Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" 
                            onClick={() => setShowRevisionDialog(true)}>
                      <RefreshCw className="w-4 h-4 mr-1" />Revizie
                    </Button>
                    <Button size="sm" className="bg-orange-600 hover:bg-orange-700" 
                            onClick={() => setShowRepairDialog(true)}>
                      <Truck className="w-4 h-4 mr-1" />Service
                    </Button>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>
          
          {selectedTool && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="grid grid-cols-6">
                <TabsTrigger value="info">Informatii</TabsTrigger>
                <TabsTrigger value="custody">Custodie</TabsTrigger>
                <TabsTrigger value="revisions">Revizii ({toolHistory.revisions.length})</TabsTrigger>
                <TabsTrigger value="repairs">Reparatii ({toolHistory.repairs.length})</TabsTrigger>
                <TabsTrigger value="documents">Documente ({toolHistory.documents.length})</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><span className="text-slate-500 text-sm">Status:</span><br/>{getStatusBadge(selectedTool.status)}</div>
                  <div><span className="text-slate-500 text-sm">Brand:</span><br/><strong>{selectedTool.brand || '-'}</strong></div>
                  <div><span className="text-slate-500 text-sm">Model:</span><br/><strong>{selectedTool.model || '-'}</strong></div>
                  <div><span className="text-slate-500 text-sm">Categorie:</span><br/><strong>{selectedTool.category || '-'}</strong></div>
                  <div><span className="text-slate-500 text-sm">Sediu:</span><br/><strong>{selectedTool.plant_name || '-'}</strong></div>
                  <div><span className="text-slate-500 text-sm">Departament:</span><br/><strong>{selectedTool.department || '-'}</strong></div>
                  <div><span className="text-slate-500 text-sm">Responsabil:</span><br/><strong>{selectedTool.assigned_to_name || '-'}</strong></div>
                  <div><span className="text-slate-500 text-sm">Cost achizitie:</span><br/><strong>{formatCurrency(selectedTool.purchase_cost)}</strong></div>
                  <div><span className="text-slate-500 text-sm">Total reparatii:</span><br/><strong>{formatCurrency(selectedTool.total_repair_cost)}</strong></div>
                </div>
                
                {selectedTool.warranty_until && (
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <Shield className="w-6 h-6 text-orange-600" />
                      <div>
                        <p className="font-medium text-orange-700">Garantie</p>
                        <p className="text-sm">Valabila pana la: <strong>{formatDate(selectedTool.warranty_until)}</strong></p>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {selectedTool.revision_required && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <p className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />Informatii Revizie
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>Ultima revizie:<br/><strong>{formatDate(selectedTool.last_revision_date)}</strong></div>
                        <div>Urmatoarea:<br/><strong>{formatDate(selectedTool.next_revision_date)}</strong></div>
                        <div>Status:<br/>{getRevisionStatusBadge(selectedTool)}</div>
                        <div>Organism:<br/><strong>{selectedTool.certification_body || '-'}</strong></div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="custody" className="space-y-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="font-medium">Responsabil actual:</p>
                    <p className="text-lg">{selectedTool.assigned_to_name || 'Nealocat'}</p>
                  </div>
                  <Button onClick={() => setShowCustodyDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                    <User className="w-4 h-4 mr-2" />Transfer Custodie
                  </Button>
                </div>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <History className="w-4 h-4" />Istoric Custodie
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {custodyHistory.length === 0 ? (
                      <p className="text-center text-slate-500 py-4">Nu exista transferuri de custodie inregistrate</p>
                    ) : (
                      <div className="space-y-3">
                        {custodyHistory.map((transfer, idx) => (
                          <div key={transfer.id} className="flex gap-3 items-start">
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                              {idx < custodyHistory.length - 1 && <div className="w-0.5 h-full bg-slate-200 mt-1"></div>}
                            </div>
                            <div className="flex-1 pb-3">
                              <p className="font-medium text-sm">
                                {transfer.from_custodian_name || 'Necunoscut'} 
                                <ChevronRight className="w-4 h-4 inline mx-1" />
                                {transfer.to_custodian_name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatDate(transfer.transfer_date)} • Transferat de {transfer.transferred_by_name || 'System'}
                              </p>
                              {transfer.notes && <p className="text-sm text-slate-600 mt-1 italic">{transfer.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="revisions" className="space-y-3 mt-4">
                {toolHistory.revisions.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nu exista revizii inregistrate</p>
                ) : (
                  toolHistory.revisions.map(rev => (
                    <Card key={rev.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-mono text-xs text-slate-400">{rev.revision_number}</p>
                            <p className="font-medium">{formatDate(rev.revision_date)}</p>
                            <p className="text-sm text-slate-500">
                              {rev.revision_type} {rev.performed_by_external && `• ${rev.performed_by_external}`}
                            </p>
                          </div>
                          <div className="text-right">
                            {rev.cost > 0 && <Badge variant="outline">{formatCurrency(rev.cost)}</Badge>}
                            <Badge className={rev.result === 'passed' ? 'bg-green-100 text-green-700 ml-2' : 
                                             rev.result === 'failed' ? 'bg-red-100 text-red-700 ml-2' : 
                                             'bg-yellow-100 text-yellow-700 ml-2'}>
                              {rev.result === 'passed' ? 'Admis' : rev.result === 'failed' ? 'Respins' : 'Conditionat'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
              
              <TabsContent value="repairs" className="space-y-3 mt-4">
                {toolHistory.repairs.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nu exista reparatii inregistrate</p>
                ) : (
                  toolHistory.repairs.map(repair => (
                    <Card key={repair.id} className="hover:border-orange-200 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-mono text-xs text-slate-400">{repair.repair_number}</p>
                            <p className="font-medium">{repair.defect_description}</p>
                            <p className="text-sm text-slate-500">
                              Trimis: {formatDate(repair.sent_date)}
                              {repair.return_date && ` → Returnat: ${formatDate(repair.return_date)}`}
                            </p>
                            {repair.service_provider && (
                              <p className="text-xs text-slate-400 mt-1">Service: {repair.service_provider}</p>
                            )}
                            {repair.work_performed && (
                              <p className="text-xs text-slate-500 mt-1">Lucrari: {repair.work_performed}</p>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              <Badge className={repair.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                {repair.status === 'completed' ? 'Finalizat' : 'In curs'}
                              </Badge>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openEditRepairDialog(repair)}
                                data-testid={`edit-repair-${repair.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                            {repair.actual_cost && <p className="text-sm font-medium">{formatCurrency(repair.actual_cost)}</p>}
                            {repair.warranty_repair && <Badge variant="outline" className="text-xs">Garantie</Badge>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
              
              <TabsContent value="documents" className="space-y-3 mt-4">
                <div className="flex justify-end mb-2">
                  <Button size="sm" variant="outline" onClick={() => setShowUploadDialog(true)}>
                    <Upload className="w-4 h-4 mr-1" />Adauga Document
                  </Button>
                </div>
                {toolHistory.documents.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nu exista documente atasate</p>
                ) : (
                  <div className="grid gap-2">
                    {toolHistory.documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">{doc.original_filename}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {getDocumentTypeBadge(doc.document_type)}
                              <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                              <span>{formatDate(doc.uploaded_at)}</span>
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadDocument(doc)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="timeline" className="mt-4">
                {toolHistory.timeline.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nu exista evenimente inregistrate</p>
                ) : (
                  <div className="space-y-3">
                    {toolHistory.timeline.map((event, idx) => (
                      <div key={event.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          {idx < toolHistory.timeline.length - 1 && <div className="w-0.5 h-full bg-slate-200 mt-1"></div>}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="font-medium text-sm">{event.title}</p>
                          <p className="text-xs text-slate-500">{formatDate(event.event_date)} • {event.created_by_name || 'System'}</p>
                          {event.description && <p className="text-sm text-slate-600 mt-1">{event.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* UPLOAD DOCUMENT DIALOG */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Incarcare Document</DialogTitle>
            <DialogDescription>Adaugati un document pentru {selectedTool?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tip document</Label>
              <Select value={uploadForm.document_type} onValueChange={(v) => setUploadForm({ ...uploadForm, document_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="certificate">Certificat</SelectItem>
                  <SelectItem value="invoice">Factura</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="photo">Fotografie</SelectItem>
                  <SelectItem value="warranty">Garantie</SelectItem>
                  <SelectItem value="report">Raport</SelectItem>
                  <SelectItem value="other">Altele</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fisier (max 50MB)</Label>
              <Input type="file" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })} />
            </div>
            <div className="space-y-2">
              <Label>Descriere</Label>
              <Input value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Anuleaza</Button>
            <Button onClick={handleUploadDocument} className="bg-blue-600 hover:bg-blue-700">Incarca</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CUSTODY TRANSFER DIALOG */}
      <Dialog open={showCustodyDialog} onOpenChange={setShowCustodyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Transfer Custodie
            </DialogTitle>
            <DialogDescription>Transferati scula catre un nou responsabil</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Scula:</p>
              <p className="font-medium">{selectedTool?.name}</p>
              <p className="text-sm text-slate-500 mt-1">Responsabil actual:</p>
              <p className="font-medium">{selectedTool?.assigned_to_name || 'Nealocat'}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Noul responsabil *</Label>
              <Input value={custodyForm.new_custodian_name} 
                     onChange={(e) => setCustodyForm({ ...custodyForm, new_custodian_name: e.target.value })} 
                     placeholder="Introduceti numele noului responsabil" />
            </div>
            
            <div className="space-y-2">
              <Label>Data transfer</Label>
              <Input type="date" value={custodyForm.transfer_date} 
                     onChange={(e) => setCustodyForm({ ...custodyForm, transfer_date: e.target.value })} />
            </div>
            
            <div className="space-y-2">
              <Label>Note / Motiv transfer</Label>
              <Textarea value={custodyForm.notes} 
                        onChange={(e) => setCustodyForm({ ...custodyForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustodyDialog(false)}>Anuleaza</Button>
            <Button onClick={handleTransferCustody} className="bg-purple-600 hover:bg-purple-700"
                    disabled={!custodyForm.new_custodian_name}>
              Confirma Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Export Format Dialog */}
      <Dialog open={showQrExportDialog} onOpenChange={setShowQrExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Export Coduri QR</DialogTitle>
            <DialogDescription>Selectati formatul si campurile vizibile pe etichete</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Se vor exporta {selectedToolIds.length > 0 ? selectedToolIds.length : tools.length} scule
            </p>
            
            {/* Field Selection */}
            <div className="border rounded-lg p-3 bg-slate-50">
              <p className="text-sm font-medium mb-2">Campuri vizibile pe eticheta:</p>
              <div className="grid grid-cols-2 gap-2">
                {qrFieldOptions.map(opt => (
                  <label key={opt.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded">
                    <Checkbox 
                      checked={qrFields[opt.key]} 
                      onCheckedChange={(checked) => setQrFields(prev => ({ ...prev, [opt.key]: checked }))}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <Separator />
            
            <div className="grid gap-3">
              <Button 
                variant="outline" 
                className="h-auto py-3 justify-start"
                onClick={() => handleExportQrCodes('standard')}
              >
                <div className="text-left">
                  <p className="font-semibold">Standard (24/pagina)</p>
                  <p className="text-xs text-slate-500">3 coloane x 8 randuri - ideal pentru A4</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 justify-start"
                onClick={() => handleExportQrCodes('large')}
              >
                <div className="text-left">
                  <p className="font-semibold">Mari (8/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 4 randuri - etichete mari vizibile</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 justify-start"
                data-testid="qr-format-industrial"
                onClick={() => handleExportQrCodes('industrial')}
              >
                <div className="text-left">
                  <p className="font-semibold">Industrial (16/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 8 randuri - 105x38mm etichete mici</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 justify-start"
                onClick={() => handleExportQrCodes('thermal')}
              >
                <div className="text-left">
                  <p className="font-semibold">Termic (50x30mm)</p>
                  <p className="text-xs text-slate-500">Pentru imprimante de etichete termice</p>
                </div>
              </Button>
              <Separator className="my-1" />
              <Button 
                variant="outline" 
                className="h-auto py-3 justify-start border-blue-200 hover:bg-blue-50"
                onClick={handleExportQrZip}
              >
                <div className="text-left">
                  <p className="font-semibold text-blue-700">Export JPEG (ZIP)</p>
                  <p className="text-xs text-slate-500">Imagini individuale cu QR + Cod + Denumire + Data revizie</p>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Fields Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Actualizare in masa</DialogTitle>
            <DialogDescription>Selectati campurile pe care doriti sa le actualizati pentru {selectedToolIds.length} scule</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 'category', label: 'Categorie', type: 'text' },
              { key: 'subcategory', label: 'Subcategorie', type: 'text' },
              { key: 'brand', label: 'Marca', type: 'text' },
              { key: 'model', label: 'Model', type: 'text' },
              { key: 'year_of_manufacture', label: 'An fabricatie', type: 'number' },
              { key: 'supplier', label: 'Furnizor', type: 'text' },
              { key: 'plant_id', label: 'Sediu', type: 'plant' },
              { key: 'location_id', label: 'Locatie', type: 'location' },
              { key: 'department', label: 'Departament', type: 'text' },
              { key: 'assigned_to_name', label: 'Atribuit catre', type: 'text' },
              { key: 'power_rating', label: 'Putere', type: 'text' },
              { key: 'revision_type', label: 'Tip revizie', type: 'text' },
              { key: 'certification_body', label: 'Organism certificare', type: 'text' },
              { key: 'notes', label: 'Observatii', type: 'text' },
            ].map(field => (
              <div key={field.key} className={`rounded-lg border p-3 transition-colors ${bulkUpdateFields[field.key] ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <Checkbox id={`bulk-tool-${field.key}`} checked={!!bulkUpdateFields[field.key]}
                    onCheckedChange={(checked) => {
                      setBulkUpdateFields(prev => ({ ...prev, [field.key]: checked }));
                      if (!checked) setBulkUpdateValues(prev => { const n = { ...prev }; delete n[field.key]; return n; });
                    }} data-testid={`bulk-tool-check-${field.key}`} />
                  <label htmlFor={`bulk-tool-${field.key}`} className="text-sm font-medium cursor-pointer flex-1">{field.label}</label>
                </div>
                {bulkUpdateFields[field.key] && (
                  <div className="mt-2 pl-6">
                    {field.type === 'plant' ? (
                      <Combobox
                        items={plants}
                        value={bulkUpdateValues.plant_id || ''}
                        onSelect={v => setBulkUpdateValues(prev => ({ ...prev, plant_id: v }))}
                        placeholder="Selecteaza sediu..."
                        searchPlaceholder="Cauta sediu..."
                      />
                    ) : field.type === 'location' ? (
                      <Combobox
                        items={locations.filter(l => !bulkUpdateValues.plant_id || l.plant_id === bulkUpdateValues.plant_id)}
                        value={bulkUpdateValues.location_id || ''}
                        onSelect={v => setBulkUpdateValues(prev => ({ ...prev, location_id: v }))}
                        placeholder="Selecteaza locatie..."
                        searchPlaceholder="Cauta locatie..."
                      />
                    ) : field.type === 'number' ? (
                      <Input type="number" value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: e.target.value }))} className="h-10" placeholder={`Noua valoare`} data-testid={`bulk-tool-val-${field.key}`} />
                    ) : (
                      <Input value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: e.target.value }))} className="h-10" placeholder={`Noua valoare ${field.label.toLowerCase()}`} data-testid={`bulk-tool-val-${field.key}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleBulkUpdateFields} disabled={!Object.values(bulkUpdateFields).some(v => v)} data-testid="bulk-update-submit-tools">
              <RefreshCw className="w-4 h-4 mr-2" />Actualizeaza {selectedToolIds.length} scule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Repair Dialog */}
      <Dialog open={showEditRepairDialog} onOpenChange={setShowEditRepairDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Editare Reparatie {selectedRepair?.repair_number}
            </DialogTitle>
            <DialogDescription>
              Modificati datele reparatiei. Toate campurile pot fi actualizate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Status</label>
                <Select value={editRepairForm.status} onValueChange={v => setEditRepairForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="edit-repair-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_progress">In curs</SelectItem>
                    <SelectItem value="completed">Finalizat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Severitate</label>
                <Select value={editRepairForm.severity} onValueChange={v => setEditRepairForm(prev => ({ ...prev, severity: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Defect Description */}
            <div>
              <label className="text-sm font-medium mb-1 block">Descriere Defect *</label>
              <Textarea 
                value={editRepairForm.defect_description} 
                onChange={e => setEditRepairForm(prev => ({ ...prev, defect_description: e.target.value }))}
                placeholder="Descrieti defectul..."
                rows={2}
                data-testid="edit-repair-defect"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Data Trimitere</label>
                <Input 
                  type="date" 
                  value={editRepairForm.sent_date} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, sent_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Data Estimata Retur</label>
                <Input 
                  type="date" 
                  value={editRepairForm.expected_return_date} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, expected_return_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Data Retur Efectiv</label>
                <Input 
                  type="date" 
                  value={editRepairForm.return_date} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, return_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Service Provider */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Furnizor Service</label>
                <Input 
                  value={editRepairForm.service_provider} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, service_provider: e.target.value }))}
                  placeholder="Numele furnizorului..."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Contact</label>
                <Input 
                  value={editRepairForm.service_contact} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, service_contact: e.target.value }))}
                  placeholder="Persoana de contact..."
                />
              </div>
            </div>

            {/* Contact details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Telefon Service</label>
                <Input 
                  value={editRepairForm.service_phone} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, service_phone: e.target.value }))}
                  placeholder="Numar telefon..."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Email Service</label>
                <Input 
                  type="email"
                  value={editRepairForm.service_email} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, service_email: e.target.value }))}
                  placeholder="Email..."
                />
              </div>
            </div>

            {/* Costs */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Cost Estimat (RON)</label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={editRepairForm.estimated_cost} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, estimated_cost: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Cost Efectiv (RON)</label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={editRepairForm.actual_cost} 
                  onChange={e => setEditRepairForm(prev => ({ ...prev, actual_cost: e.target.value }))}
                  placeholder="0.00"
                  data-testid="edit-repair-actual-cost"
                />
              </div>
              <div className="flex items-center pt-6">
                <Checkbox 
                  id="edit-warranty" 
                  checked={editRepairForm.warranty_repair}
                  onCheckedChange={checked => setEditRepairForm(prev => ({ ...prev, warranty_repair: checked }))}
                />
                <label htmlFor="edit-warranty" className="ml-2 text-sm">Reparatie in garantie</label>
              </div>
            </div>

            {/* Work performed */}
            <div>
              <label className="text-sm font-medium mb-1 block">Lucrari Efectuate</label>
              <Textarea 
                value={editRepairForm.work_performed} 
                onChange={e => setEditRepairForm(prev => ({ ...prev, work_performed: e.target.value }))}
                placeholder="Descrieti lucrarile efectuate..."
                rows={2}
                data-testid="edit-repair-work"
              />
            </div>

            {/* Parts replaced */}
            <div>
              <label className="text-sm font-medium mb-1 block">Piese Inlocuite</label>
              <Textarea 
                value={editRepairForm.parts_replaced} 
                onChange={e => setEditRepairForm(prev => ({ ...prev, parts_replaced: e.target.value }))}
                placeholder="Lista piese inlocuite..."
                rows={2}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-1 block">Observatii</label>
              <Textarea 
                value={editRepairForm.notes} 
                onChange={e => setEditRepairForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Alte observatii..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRepairDialog(false)}>Anuleaza</Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700" 
              onClick={handleEditRepair}
              disabled={!editRepairForm.defect_description}
              data-testid="edit-repair-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />Salveaza Modificarile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ToolsPage;
